import { config } from '../config';
import { FormValidation } from '../typings';
import type { SingleFormField } from '../typings';

/**
 * Rules the API enforces on write, mirroring UpdateFieldService's
 * _ValidateTextRestrictions / _ValidateNumberRestrictions / _ValidateDateRestrictions.
 * Required and MinLength are absent on purpose: the server stores a blank value
 * without checking anything, and never receives a MinLength restriction at all.
 */
const SERVER_ENFORCED: ReadonlySet<string> = new Set([
  FormValidation.Email,
  FormValidation.Url,
  FormValidation.MaxLength,
  FormValidation.Regex,
  FormValidation.MinValue,
  FormValidation.MaxValue,
]);

type EnforcedRule = { type: FormValidation; value?: number | string };

const isBlank = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

/** The separate values the API will receive for one Formik value. */
const splitParts = (value: unknown): string[] => {
  const normalized = Array.isArray(value)
    ? value.map(String).join(config.multiValueDivider)
    : String(value);

  // Whitespace-only parts are dropped, not trimmed: the API skips a blank value
  // entirely (IsNullOrWhiteSpace) but measures every other value exactly as sent.
  return normalized
    .split(config.multiValueDivider)
    .filter(part => part.trim() !== '');
};

/**
 * The provider-authored text inside one part, or null when the API supplies the
 * text itself (a lookup token) and the provider's characters are not what gets stored.
 */
const providerText = (part: string): string | null => {
  if (part.startsWith(config.internalLookupPrefix)) return null;
  if (part.startsWith(config.externalLookupPrefix)) return null;

  return part.startsWith(config.customValuePrefix)
    ? part.slice(config.customValuePrefix.length)
    : part;
};

/** Provider-authored strings the API will validate for this value. */
const validatedTexts = (value: unknown): string[] =>
  splitParts(value)
    .map(providerText)
    // Blankness is judged after the prefix is stripped: "[^]   " is a blank
    // custom entry, which the API skips rather than measures.
    .filter((text): text is string => text !== null && text.trim() !== '');

// Deliberately loose, matching the server's Uri.TryCreate(Absolute) rather than
// yup's stricter form: a value yup rejects but the API accepts must still save.
const isAbsoluteUrl = (text: string): boolean => {
  try {
    new URL(text);
    return true;
  } catch {
    return false;
  }
};

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const asNumber = (raw: unknown): number | null => {
  // Number('') is 0; an absent bound must read as "no bound", not zero.
  if (typeof raw === 'string' && raw.trim() === '') return null;

  const parsed = typeof raw === 'number' ? raw : Number(String(raw));
  return Number.isFinite(parsed) ? parsed : null;
};

// Year-first (the Formik value, `YYYY-MM-DD`) and month-first (the API's bound,
// `MM/dd/yyyy`). Either separator, because seeds carry both.
const YEAR_FIRST_DATE = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;
const MONTH_FIRST_DATE = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/;

/**
 * A date string as local midnight on its calendar day, matching the server's
 * DateOnly. Built from components rather than parsed: `new Date` reads
 * `YYYY-MM-DD` as UTC and `MM/dd/yyyy` as local, so the two sides of a bound
 * would sit a UTC offset apart and reject a value landing exactly on it.
 * Any other shape returns null so the rule fails open.
 */
const asDate = (raw: unknown): number | null => {
  const text = String(raw).trim();

  const yearFirst = YEAR_FIRST_DATE.exec(text);
  if (yearFirst) {
    const [, year, month, day] = yearFirst;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }

  const monthFirst = MONTH_FIRST_DATE.exec(text);
  if (monthFirst) {
    const [, month, day, year] = monthFirst;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }

  return null;
};

/**
 * Compares a value against a MinValue/MaxValue bound as a number when both read as
 * numbers, and as a date otherwise. Returns null when neither comparison applies.
 */
const compareToBound = (
  value: unknown,
  bound: number | string,
): number | null => {
  const boundNumber = asNumber(bound);
  const valueNumber = asNumber(value);

  if (boundNumber !== null && valueNumber !== null) {
    return valueNumber - boundNumber;
  }

  const boundDate = asDate(bound);
  const valueDate = asDate(value);

  if (boundDate !== null && valueDate !== null) {
    return valueDate - boundDate;
  }

  return null;
};

/** The rule this value violates and would be rejected for, or null. */
const findViolatedRule = (
  field: SingleFormField,
  value: unknown,
): EnforcedRule | null => {
  if (isBlank(value)) return null;

  const rules = (field.validations ?? []).filter(rule =>
    SERVER_ENFORCED.has(rule.type),
  ) as EnforcedRule[];

  if (rules.length === 0) return null;

  const texts = validatedTexts(value);

  for (const rule of rules) {
    switch (rule.type) {
      case FormValidation.MaxLength: {
        // A numeric field's MaxLength is a digit count of the rounded value,
        // not a text length. Reproducing that is not worth it for a
        // configuration no seed uses, so defer to the API.
        if ('inputType' in field && field.inputType === 'number') break;

        // Measured on the formatted value while the API stores the unmasked one,
        // so a masked field's limit has to clear the formatted length.
        if (texts.some(text => text.length > Number(rule.value))) return rule;
        break;
      }

      case FormValidation.Email:
        if (texts.some(text => !EMAIL_PATTERN.test(text))) return rule;
        break;

      case FormValidation.Url:
        if (texts.some(text => !isAbsoluteUrl(text))) return rule;
        break;

      case FormValidation.Regex: {
        // An unparseable pattern fails open: the API is the authority, and
        // throwing here would break autosave on every keystroke.
        let pattern: RegExp;
        try {
          pattern = new RegExp(String(rule.value));
        } catch {
          break;
        }

        if (texts.some(text => !pattern.test(text))) return rule;
        break;
      }

      case FormValidation.MinValue: {
        const diff = compareToBound(value, rule.value as number | string);
        if (diff !== null && diff < 0) return rule;
        break;
      }

      case FormValidation.MaxValue: {
        const diff = compareToBound(value, rule.value as number | string);
        if (diff !== null && diff > 0) return rule;
        break;
      }
    }
  }

  return null;
};

/** True when the API would accept this value for this field. Blank always passes. */
export const isPersistable = (
  field: SingleFormField,
  value: unknown,
): boolean => findViolatedRule(field, value) === null;

/** Why the API would refuse this value, phrased as a consequence, or null if it wouldn't. */
export const describeBlockedEdit = (
  field: SingleFormField,
  value: unknown,
): string | null => {
  const rule = findViolatedRule(field, value);
  if (rule === null) return null;

  switch (rule.type) {
    case FormValidation.MaxLength: {
      const longest = Math.max(
        ...validatedTexts(value).map(text => text.length),
      );
      return `It's ${longest} characters; the limit is ${rule.value}.`;
    }
    case FormValidation.Email:
      return "It isn't a valid email address.";
    case FormValidation.Url:
      return "It isn't a valid web address.";
    case FormValidation.MinValue:
      return `It's below the minimum of ${rule.value}.`;
    case FormValidation.MaxValue:
      return `It's above the maximum of ${rule.value}.`;
    default:
      return "It isn't in the format this field accepts.";
  }
};
